import React, { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import useLoader from "../../../hooks/useLoader";
import { detailsSupplierFields } from "../../../constants/forms/supplierFields";
import { usePermissions } from "../../../hooks/usePermissions";
import { setDefaultFieldValues } from "../../../utils/objectUtils";
import useForm from "../../../hooks/useForm";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import useAction from "../../../hooks/useAction";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import { maskCep, maskCpfCnpj, maskPhone } from "../../../utils/maskUtils";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useNotification from "../../../hooks/useNotification";
import useUrlFilters from "../../../hooks/useUrlFilters";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const DetailsSupplierPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { canAccess } = usePermissions();
  const {
    getByColumn: baseServiceGetById,
    get: baseServiceGet,
    del: baseServiceDelete,
  } = useBaseService(navigate);

  // Estado do formulário de detalhes (não muda para TanStack Query)
  const { formData, formatData, setFormData } = useForm(
    setDefaultFieldValues(detailsSupplierFields),
  );

  // --- Padronização de filtros e paginação para ENDEREÇOS ---
  const {
    filters: addressFilters,
    setFilters: setAddressUrlFilters,
    pagination: addressPagination,
    changePage: changeAddressPage,
    setPagination: setAddressPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    `almox_supplierAddresses`,
  );

  const { localFilterValues: addressLocalFilterValues } = useGenericFilter({
    deleted_at: false,
  });

  // --- Padronização de filtros e paginação para CONTATOS ---
  const {
    filters: contactFilters,
    setFilters: setContactUrlFilters,
    pagination: contactPagination,
    changePage: changeContactPage,
    setPagination: setContactPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    `almox_supplierContacts`,
  );

  const { localFilterValues: contactLocalFilterValues } = useGenericFilter({
    deleted_at: false,
  });

  // --- Query para buscar os detalhes do Fornecedor ---
  const {
    data: supplierData,
    isLoading: isLoadingSupplier,
    isError: isErrorSupplier,
    error: supplierError,
  } = useQueryWithLoader({
    queryKey: ["almox_supplier", id],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetById(entities.suppliers.getById(id));
      return response.result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  // --- useEffect para carregar os detalhes do fornecedor ---
  useEffect(() => {
    if (
      !isLoadingSupplier &&
      !isErrorSupplier &&
      !supplierError &&
      supplierData
    ) {
      formatData(supplierData, detailsSupplierFields);
      setFormData((prev) => ({
        ...prev,
        cpf_cnpj: maskCpfCnpj(supplierData.cpf_cnpj),
      }));
      hideLoader();
    }

    if (isErrorSupplier || supplierError) {
      console.error("Erro ao carregar detalhes do fornecedor:", supplierError);
      showNotification(
        "error",
        `Erro ao carregar detalhes do fornecedor: ${
          supplierError.response?.data?.message || supplierError.message
        }`,
      );
      hideLoader();
      navigate(`/almoxarifado/fornecedores`, {
        state: {
          type: "error",
          message: "Fornecedor não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [
    supplierData,
    isLoadingSupplier,
    isErrorSupplier,
    supplierError,
    formatData,
    setFormData,
    hideLoader,
    showNotification,
    navigate,
  ]);

  // --- Query para buscar Endereços do Fornecedor ---
  const {
    data: addressesData,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
    isError: isErrorAddresses,
    error: addressesError,
  } = useQueryWithLoader({
    queryKey: [
      "almox_supplierAddresses",
      id,
      addressFilters,
      addressPagination,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(addressFilters);
      const params = {
        filters: filtersTransformed,
        page: addressPagination.page,
        perPage: addressPagination.perPage,
      };
      const response = await baseServiceGet(
        entities.suppliers.addresses.get(id),
        params,
      ); // Reutilizando fetchById para nested resources
      return response.result;
    },
    select: (data) => {
      return {
        addresses: data.data.map((address) => ({
          id: address.id,
          alias: address.alias,
          street: address.street,
          number: address.number,
          zip: maskCep(address.zip),
          isDeleted: !!address.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
    staleTime: 60 * 1000,
  });

  const { addresses = [], totalPagesAddress = 1 } = addressesData || {};

  const addressColumns = [
    { key: "alias", label: "Apelido" },
    { key: "street", label: "Rua" },
    { key: "number", label: "Número" },
    { key: "zip", label: "CEP" },
  ];

  // --- Query para buscar Contatos do Fornecedor ---
  const {
    data: contactsData,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
    isError: isErrorContacts,
    error: contactsError,
  } = useQueryWithLoader({
    queryKey: ["almox_supplierContacts", id, contactFilters, contactPagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(contactFilters);
      const params = {
        filters: filtersTransformed,
        page: contactPagination.page,
        perPage: contactPagination.perPage,
      };
      const response = await baseServiceGet(
        entities.suppliers.contacts.get(id),
        params,
      );
      return response.result;
    },
    select: (data) => {
      return {
        contacts: data.data.map((contact) => ({
          id: contact.id,
          name: contact.name,
          phone: maskPhone(contact.phone),
          cell: maskPhone(contact.cell),
          email: contact.email,
          isDeleted: !!contact.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
    staleTime: 60 * 1000,
  });

  const contactColumns = [
    { key: "name", label: "Nome" },
    { key: "phone", label: "Telefone" },
    { key: "cell", label: "Celular" },
    { key: "email", label: "Email" },
  ];

  const { contacts = [], totalPagesContact = 1 } = contactsData || {};

  // --- Gerenciamento de Loader para todas as queries ---
  useEffect(() => {
    if (
      isLoadingSupplier ||
      isLoadingAddresses ||
      isFetchingAddresses ||
      isLoadingContacts ||
      isFetchingContacts
    ) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [
    isLoadingSupplier,
    isLoadingAddresses,
    isFetchingAddresses,
    isLoadingContacts,
    isFetchingContacts,
    showLoader,
    hideLoader,
  ]);

  useEffect(() => {
    if (isErrorContacts) {
      console.error(
        "DetailsSupplierPage: Erro ao carregar contatos (useQuery):",
        contactsError,
      );
      showNotification(
        "error",
        `Erro ao carregar contatos: ${
          contactsError.response?.data?.message || contactsError.message
        }`,
      );
    }
    if (isErrorAddresses) {
      console.error(
        "DetailsSupplierPage: Erro ao carregar endereços (useQuery):",
        addressesError,
      );
      showNotification(
        "error",
        `Erro ao carregar endereços: ${
          addressesError.response?.data?.message || addressesError.message
        }`,
      );
    }
  }, [
    isErrorContacts,
    contactsError,
    showNotification,
    isErrorAddresses,
    addressesError,
  ]);

  // --- Refetchers para as listas de endereços e contatos ---
  const refreshAddresses = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [
        "almox_supplierAddresses",
        id,
        addressFilters,
        addressPagination,
      ],
    });
  }, [queryClient, id, addressFilters, addressPagination]);

  const refreshContacts = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [
        "almox_supplierContacts",
        id,
        contactFilters,
        contactPagination,
      ],
    });
  }, [queryClient, id, contactFilters, contactPagination]);

  // --- Ações de Confirmação (reutilizando useAction) ---
  const {
    openModalConfirmation: openModalConfirmationAddress,
    action: actionAddress,
    handleDelete: handleDeleteAddress,
    showConfirmation: showConfirmationAddress,
    handleConfirmAction: handleConfirmActionAddress,
    handleCancelConfirmation: handleCancelConfirmationAddress,
    selectedItem: selectedAddress,
  } = useAction(navigate);

  const {
    openModalConfirmation: openModalConfirmationContact,
    action: actionContact,
    handleDelete: handleDeleteContact,
    showConfirmation: showConfirmationContact,
    handleConfirmAction: handleConfirmActionContact,
    handleCancelConfirmation: handleCancelConfirmationContact,
    selectedItem: selectedContact,
  } = useAction(navigate);

  const activateAddressMutation = useMutation({
    mutationFn: async (addressId) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.suppliers.addresses.delete(id, addressId),
      );
      return response;
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshAddresses();
    },
    onError: (err) => {
      console.log("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmationAddress();
    },
  });

  const activateContactMutation = useMutation({
    mutationFn: async (contactId) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.suppliers.contacts.delete(id, contactId),
      );
      return response;
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshContacts();
    },
    onError: (err) => {
      console.log("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmationContact();
    },
  });

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      // Limpa o estado para que a mensagem não apareça novamente ao navegar
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const addressActions = useMemo(
    () => [
      {
        id: "viewAddresses",
        icon: faEye,
        title: "Ver endereço",
        buttonClass: "btn-info",
        permission: "Ver endereços de fornecedores",
        onClick: (address) =>
          navigate(
            `/almoxarifado/fornecedores/${id}/endereco/${address.id}/detalhes/`,
          ),
        show: (address) =>
          !address.isDeleted && canAccess("Ver endereços de fornecedores"),
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar endereços de fornecedores",
        onClick: (address) =>
          navigate(
            `/almoxarifado/fornecedores/${id}/endereco/${address.id}/editar`,
          ),
        show: (address) =>
          !address.isDeleted &&
          canAccess("Atualizar endereços de fornecedores"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir endereços de fornecedores",
        onClick: (address) =>
          handleDeleteAddress(
            address,
            entities.suppliers.addresses.delete(id, address.id),
            refreshAddresses,
          ),
        show: (address) =>
          !address.isDeleted && canAccess("Excluir endereços de fornecedores"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir endereços de fornecedores",
        onClick: (address) =>
          showConfirmationAddress(
            address,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateAddressMutation.mutateAsync(address.id);
              } catch (error) {
                console.log("Erro ao restaurar endereço:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (address) =>
          address.isDeleted && canAccess("Excluir endereços de fornecedores"),
      },
    ],
    [
      navigate,
      id,
      canAccess,
      handleDeleteAddress,
      refreshAddresses,
      showConfirmationAddress,
      activateAddressMutation,
    ],
  );

  const contactActions = useMemo(
    () => [
      {
        id: "viewContacts",
        icon: faEye,
        title: "Ver contato",
        buttonClass: "btn-info",
        permission: "Ver contatos de fornecedores",
        onClick: (contact) =>
          navigate(
            `/almoxarifado/fornecedores/${id}/contato/${contact.id}/detalhes/`,
          ),
        show: (contact) =>
          !contact.isDeleted && canAccess("Ver contatos de fornecedores"),
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar contatos de fornecedores",
        onClick: (contact) =>
          navigate(
            `/almoxarifado/fornecedores/${id}/contato/editar/${contact.id}`,
          ),
        show: (contact) =>
          !contact.isDeleted && canAccess("Atualizar contatos de fornecedores"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir contatos de fornecedores",
        onClick: (contact) =>
          handleDeleteContact(
            contact,
            entities.suppliers.contacts.delete(id, contact.id),
            refreshContacts,
          ),
        show: (contact) =>
          !contact.isDeleted && canAccess("Excluir contatos de fornecedores"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir contatos de fornecedores",
        onClick: (contact) =>
          showConfirmationContact(
            contact,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateContactMutation.mutateAsync(contact.id);
              } catch (error) {
                console.log("Erro ao restaurar contato:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (contact) =>
          contact.isDeleted && canAccess("Excluir contatos de fornecedores"),
      },
    ],
    [
      navigate,
      id,
      canAccess,
      handleDeleteContact,
      refreshContacts,
      showConfirmationContact,
      activateContactMutation,
    ],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Fornecedor"
        showBackButton={true}
        backUrl={`/almoxarifado/fornecedores`}
      />
      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          sections={detailsSupplierFields}
          formData={formData}
          isLoading={isLoadingSupplier}
          readOnly={true}
        />

        <ListHeader
          title="Endereços"
          buttonText="Adicionar Endereço"
          buttonLink={`/almoxarifado/fornecedores/${id}/endereco/adicionar`}
          canAccess={canAccess}
          permission="Criar endereços de fornecedores"
        />

        <TableWithInactiveFilter
          data={addresses}
          columns={addressColumns}
          actions={addressActions}
          totalPages={totalPagesAddress}
          currentPage={addressPagination.page - 1}
          onPageChange={changeAddressPage}
          rowsPerPage={addressPagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setAddressPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={addressLocalFilterValues}
          localFilterValues={addressLocalFilterValues}
          applyUrlFilters={setAddressUrlFilters}
          type="default"
          isLoading={isLoadingAddresses}
          isFetching={isFetchingAddresses}
        />

        <ListHeader
          title="Contatos"
          buttonText="Adicionar Contato"
          buttonLink={`/almoxarifado/fornecedores/${id}/contato/adicionar`}
          canAccess={canAccess}
          permission="Criar contatos de fornecedores"
        />

        <TableWithInactiveFilter
          data={contacts}
          columns={contactColumns}
          actions={contactActions}
          totalPages={totalPagesContact}
          currentPage={contactPagination.page - 1}
          onPageChange={changeContactPage}
          rowsPerPage={contactPagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setContactPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={contactLocalFilterValues}
          localFilterValues={contactLocalFilterValues}
          applyUrlFilters={setContactUrlFilters}
          type="default"
          isLoading={isLoadingContacts}
          isFetching={isFetchingContacts}
        />

        <ConfirmationModal
          open={openModalConfirmationAddress}
          onClose={handleCancelConfirmationAddress}
          onConfirm={handleConfirmActionAddress}
          itemName={
            selectedAddress
              ? `${selectedAddress.street} - ${selectedAddress.zip}`
              : ""
          }
          text={actionAddress.text}
        />

        <ConfirmationModal
          open={openModalConfirmationContact}
          onClose={handleCancelConfirmationContact}
          onConfirm={handleConfirmActionContact}
          itemName={selectedContact ? selectedContact.name : ""}
          text={actionContact.text}
        />
      </div>
    </MainLayout>
  );
};

export default DetailsSupplierPage;
